import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { cttqService, formatCoveragePercent, type CttqPortfolioHealth } from '../../services/cttqService';
import { resolveCttqAlertTone } from '../../services/cttqService';
import type { TtqSubjectCode } from '../../services/ttqService';
import { SectionCard } from './SectionCard';

type CttqListHealthBannerProps = {
  subject: TtqSubjectCode;
};

export function CttqListHealthBanner({ subject }: CttqListHealthBannerProps) {
  const [health, setHealth] = useState<CttqPortfolioHealth | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void cttqService.getPortfolioHealth(subject)
      .then((data) => {
        if (!cancelled) setHealth(data);
      })
      .catch(() => {
        if (!cancelled) setHealth(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [subject]);

  if (loading) {
    return (
      <SectionCard tone="muted">
        <div className="flex items-center gap-2 text-sm text-muted">
          <Loader2 className="animate-spin" size={16} />
          正在加载收敛健康度…
        </div>
      </SectionCard>
    );
  }

  if (!health || health.alertLevel === 'ok') {
    return null;
  }

  const tone = resolveCttqAlertTone(health.alertLevel);
  return (
    <SectionCard
      title="收敛健康度提示"
      subtitle={`收敛比 ${health.convergenceRatio.toFixed(2)}（${health.activeCttqCount} CTTQ / ${health.eligibleMemberCount} 可收敛 member）`}
      tone={tone === 'danger' ? 'muted' : 'accent'}
      className={tone === 'danger' ? 'border-danger/40' : 'border-amber-400/50'}
    >
      <p className="text-sm text-muted">
        CTTQ 过多时建议合并或归档，避免反超 member 数量。
        {health.alertLevel === 'critical' ? ' 当前为红色告警，请优先整理已有聚合题。' : null}
      </p>
      <p className="text-xs text-muted">
        平均 member/CTTQ：{health.avgMembersPerCttq.toFixed(1)} · 低优先级占比 {Math.round(health.deprioritizedShare * 100)}%
      </p>
    </SectionCard>
  );
}

export function CttqSessionHealthBanner({ health }: { health: CttqPortfolioHealth | null }) {
  if (!health) return null;
  const warn = health.alertLevel !== 'ok';
  return (
    <div className={`rounded-xl border px-4 py-3 text-sm ${warn ? 'border-amber-400/60 bg-amber-50' : 'border-border bg-canvas'}`}>
      <p className="text-ink">
        收敛比 {health.convergenceRatio.toFixed(2)}（{health.activeCttqCount} CTTQ / {health.eligibleMemberCount} member）
      </p>
      {warn ? (
        <p className="mt-1 text-muted">CTTQ 过多时建议合并或归档，避免反超 member 数量。</p>
      ) : null}
    </div>
  );
}

export { formatCoveragePercent };
